import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AiGenerativeSlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';

import { Ati } from '@atlassian/ari';
import {
	ExperienceName,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation';

import {
	type ExperimentalPromotedSpacesData,
	experimentalPromotedSpacesQuery,
} from '@confluence/legacy-schema-bridge/entry-points/experimentalPromotedSpacesQuery.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { usePreloadSmartCreationModal } from '@confluence/smart-create/entry-points/usePreloadSmartCreationModal.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';

import { ItemTitleWithLozenge } from '../ItemTitle.tsx';
import { useUniversalCreateState } from '../useUniversalCreate.ts';
import type { GetPromotedSpacesQueryVariables } from '../../queries/__types__/GetPromotedSpacesQuery.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

const i18n = defineMessages({
	slideTitle: {
		id: 'create-blank-fabric-page.create.content.slides.title',
		defaultMessage: 'Slides',
		description:
			'Title for the Slides option in the create menu, which opens Create with Rovo with the slides content type pre-selected',
	},
	labsLozenge: {
		id: 'create-blank-fabric-page.create.content.slides.labs-lozenge',
		defaultMessage: 'LABS',
		description: 'Lozenge label indicating the Slides feature is in labs',
	},
});

const getSpaceKeyForCreation = async (): Promise<string> => {
	const spaceKeyRequest: GetPromotedSpacesQueryVariables = {
		promotedSpaceKey: undefined,
	};

	const spaceKeyResponse: ExperimentalPromotedSpacesData = (
		await experimentalPromotedSpacesQuery({
			variables: { ...spaceKeyRequest, promotedSpacesLimit: 1, otherSpacesLimit: 1 },
		})
	).data;

	return spaceKeyResponse?.experimentalPromotedSpaces?.nodes?.[0]?.id ?? '';
};

/**
 * Menu item that opens the Create with Rovo dialog with the Slides outcome type pre-selected.
 * Only shown when both the outcome type picker experiment and the slides experiment are active.
 */
export function useSlideItem({
	spaceKey,
	onClick,
	source,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { openModal } = useSmartCreationModalActions();
	const { isOpen } = useUniversalCreateState();
	const experienceTracker = useAIMateExperienceTracker();
	const preloadSmartCreationModal = usePreloadSmartCreationModal();

	const [fetchedSpaceKey, setFetchedSpaceKey] = useState<string>('');

	useEffect(() => {
		void getSpaceKeyForCreation().then(setFetchedSpaceKey);
	}, []);

	useEffect(() => {
		if (isOpen) {
			preloadSmartCreationModal();
		}
	}, [isOpen, preloadSmartCreationModal]);

	const handleOpenSlideCreation = useCallback(() => {
		experienceTracker.start({
			name: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			id: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			attributes: {
				product: 'confluence',
				source,
			},
		});

		openModal({
			entryPoint: source,
			spaceKey: spaceKey ?? fetchedSpaceKey,
			initialOutcomeType: 'slides',
		});
	}, [experienceTracker, fetchedSpaceKey, openModal, source, spaceKey]);

	const handleItemOnClick = useCallback(
		(e: any, actionSubjectId: string, attributes?: Record<string, string>) => {
			onClick(actionSubjectId, attributes);

			if (e?.metaKey || e?.ctrlKey) {
				return;
			}

			e.preventDefault();
			handleOpenSlideCreation();
		},
		[onClick, handleOpenSlideCreation],
	);

	const href = HOME.toUrl({}, { query: { openCreateWithRovo: 'true' } });

	return {
		ati: Ati.parse(`ati:cloud:confluence:slide`),
		children: (
			<ItemTitleWithLozenge lozenge={formatMessage(i18n.labsLozenge)}>
				{formatMessage(i18n.slideTitle)}
			</ItemTitleWithLozenge>
		),
		onClick: (e: any) => handleItemOnClick(e, 'createSlidePopoverItem'),
		href,
		elemBefore: AiGenerativeSlidesIcon,
		testId: `create-menu-slide-link-item`,
	};
}
